import { useEffect, useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import {
  LOCALITY_SEARCH_MIN_CHARS,
  useLocalitySearch,
} from "@/features/orders/hooks/useLocalitySearch";
import {
  useSetLocality,
  type LocalityTarget,
} from "@/features/orders/hooks/useSetLocality";
import { shortenDistrict } from "@/features/orders/lib/locality";
import { extractApiError, extractApiErrorMessage } from "@/lib/apiErrors";
import { cn } from "@/lib/cn";
import { toastError, toastId } from "@/lib/toast";
import type { LocalityRef, LocalityStatus } from "@/types/localities";

/** `null` = reset to automatic detection. */
const localityFormSchema = z.object({
  locality_id: z.number().int().positive().nullable(),
});
type LocalityFormValues = z.infer<typeof localityFormSchema>;

interface Props {
  target: LocalityTarget;
  status: LocalityStatus;
  /** The row's current locality id, marked with a check when listed. */
  currentId: number | null;
  /** Closes the picker; focus goes back to the edit button. */
  onClose: () => void;
}

const TEXT_BUTTON =
  "min-h-[32px] rounded px-1 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 aria-disabled:cursor-not-allowed aria-disabled:opacity-60";

/**
 * Manual locality picker (Sprint 228, TASK-5176, design spec §3).
 *
 * Inline, in place of the locality line — not a popover: the repo has no Radix
 * Popover, and an in-flow list cannot be clipped by the detail modal's
 * scrolling body. WAI-ARIA 1.2 "list autocomplete": the input keeps focus, the
 * active option is announced through `aria-activedescendant`.
 *
 * Choosing an option IS the commit (no «Сохранить»): one decision, one gesture,
 * and the change is reversible. The form (react-hook-form + zod) carries only
 * `locality_id`; the search text is a query, not a value, so it is local state.
 *
 * Escape is handled here and STOPPED, so the Radix Dialog around the card does
 * not close and throw the card away: with the list open it closes the list,
 * with the list closed it cancels editing. Radix listens for Escape on
 * `document` in the CAPTURE phase, which runs before any React handler — a
 * `stopPropagation()` in `onKeyDown` arrives too late and the modal closes.
 * The picker therefore listens on `window` in the capture phase (which runs
 * before `document`'s) and stops the event there.
 */
export function LocalityPicker({ target, status, currentId, onClose }: Props) {
  const { t } = useTranslation();
  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;
  const statusId = `${baseId}-status`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  // Typed through React, not the DOM global: ESLint's browser-global list in
  // this repo does not declare `HTMLFormElement`.
  const formRef = useRef<React.ComponentRef<"form">>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [query, setQuery] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const search = useLocalitySearch(query);
  const results: LocalityRef[] = search.active ? (search.data ?? []) : [];

  const mutation = useSetLocality(target);
  const {
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<LocalityFormValues>({
    resolver: zodResolver(localityFormSchema),
    defaultValues: { locality_id: null },
  });

  useEffect(() => {
    inputRef.current?.focus();
    return () => {
      if (blurTimer.current) clearTimeout(blurTimer.current);
    };
  }, []);

  // A fresh result set starts with nothing highlighted; the list opens on the
  // first results and stays open while typing.
  useEffect(() => {
    setActiveIndex(-1);
    if (search.active && (search.data?.length ?? 0) > 0 && !search.isError) {
      setListOpen(true);
    }
  }, [search.data, search.active, search.isError]);

  const saving = mutation.isPending;
  const showList = listOpen && results.length > 0 && !saving;

  // Latest Escape behaviour, read by the window listener below without
  // re-registering it on every render.
  const escapeRef = useRef<() => void>(() => {});
  escapeRef.current = () => {
    if (showList) {
      setListOpen(false);
      setActiveIndex(-1);
    } else if (!saving) {
      onClose();
    }
  };

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      const form = formRef.current;
      if (!form || !(e.target instanceof Node) || !form.contains(e.target)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      escapeRef.current();
    }
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  async function submit(values: LocalityFormValues) {
    clearErrors("locality_id");
    try {
      await mutation.mutateAsync({ locality_id: values.locality_id });
      onClose();
    } catch (err) {
      const result = extractApiError(err, t);
      const fieldError = result.fieldErrors.locality_id;
      if (fieldError) {
        // A 400 on `locality_id` (unknown or inactive id) belongs on the field.
        setError("locality_id", { type: "server", message: fieldError });
      } else {
        toastError(
          t("orders.locality.toast.failed"),
          extractApiErrorMessage(err, t),
          { id: toastId("error", "locality", `${target.kind}:${target.id}`) },
        );
      }
      inputRef.current?.focus();
    }
  }

  function select(option: LocalityRef) {
    if (saving) return;
    setListOpen(false);
    setValue("locality_id", option.id);
    void handleSubmit(submit)();
  }

  function resetToAuto() {
    if (saving) return;
    setListOpen(false);
    setValue("locality_id", null);
    // Focus the (read-only while saving) input so the Escape guard keeps
    // holding for the whole PATCH, whichever control started it.
    inputRef.current?.focus();
    void handleSubmit(submit)();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!listOpen) {
          if (results.length > 0) setListOpen(true);
          return;
        }
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
        return;
      case "ArrowUp":
        e.preventDefault();
        if (listOpen) setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      case "Enter":
        // Never submits an empty form: with no active option, Enter is inert.
        e.preventDefault();
        if (showList && activeIndex >= 0 && results[activeIndex]) {
          select(results[activeIndex]);
        }
        return;
      case "Tab":
        setListOpen(false);
        return;
      default:
        return;
    }
  }

  // One status line, announced politely (spec §3.4).
  let statusText: string;
  let statusIsError = false;
  if (saving) {
    statusText = t("orders.locality.saving");
  } else if (query.trim().length < LOCALITY_SEARCH_MIN_CHARS) {
    statusText = t("orders.locality.picker.minChars");
  } else if (search.isError) {
    statusText = `${t("orders.locality.picker.searchFailed")} — ${extractApiErrorMessage(search.error, t)}`;
    statusIsError = true;
  } else if (search.isDebouncing || search.isFetching || !search.data) {
    statusText = t("orders.locality.picker.loading");
  } else if (results.length === 0) {
    statusText = t("orders.locality.picker.empty", {
      query: search.debouncedQuery,
    });
  } else {
    statusText = t("orders.locality.picker.found", { count: results.length });
  }

  const busy = saving || (search.active && search.isFetching);
  const fieldError = errors.locality_id?.message;

  return (
    <form
      ref={formRef}
      noValidate
      onSubmit={(e) => e.preventDefault()}
      className="space-y-1"
      data-testid="locality-picker"
    >
      <label htmlFor={inputId} className="text-xs font-semibold text-gray-700">
        {t("orders.locality.picker.label")}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            showList && activeIndex >= 0 ? optionId(activeIndex) : undefined
          }
          aria-describedby={statusId}
          aria-invalid={fieldError ? true : undefined}
          aria-busy={saving || undefined}
          // NOT `disabled`: disabling the focused input drops focus to <body>,
          // and an Escape then reaches the Radix Dialog and closes the whole
          // card mid-save. Read-only keeps focus inside the picker, whose
          // window-level Escape guard keeps holding (code review, item 6).
          aria-disabled={saving || undefined}
          readOnly={saving}
          autoComplete="off"
          value={query}
          placeholder={t("orders.locality.picker.placeholder")}
          onChange={(e) => {
            setQuery(e.target.value);
            if (fieldError) clearErrors("locality_id");
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setListOpen(true);
          }}
          onBlur={() => {
            // Delayed so a click on an option still lands.
            blurTimer.current = setTimeout(() => setListOpen(false), 150);
          }}
          className={cn(
            "h-10 w-full rounded-xl border px-3 pr-9 text-sm text-gray-900 outline-none transition-colors",
            "focus:border-brand-primary focus:ring-1 focus:ring-brand-primary",
            "aria-disabled:cursor-not-allowed aria-disabled:bg-gray-50",
            fieldError ? "border-brand-danger" : "border-gray-200",
          )}
        />
        {busy && (
          <span
            aria-hidden="true"
            className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-2 border-gray-300 border-t-brand-primary"
          />
        )}
      </div>

      {/* Always mounted (hidden when empty) so `aria-controls` never points at
          nothing. In flow, not absolute: the modal body scrolls and would clip
          an overlay at 375px. */}
      <ul
        id={listboxId}
        role="listbox"
        aria-label={t("orders.locality.picker.label")}
        hidden={!showList}
        className="mt-1 max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-sm"
      >
        {showList &&
          results.map((option, index) => {
            const district = shortenDistrict(option.district);
            const hasSecondary = Boolean(district || option.region);
            const isCurrent = option.id === currentId;
            return (
              <li
                key={option.id}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(option);
                }}
                onMouseEnter={() => setActiveIndex(index)}
                className={cn(
                  "flex min-h-[40px] cursor-pointer items-start gap-2 px-3 py-2",
                  index === activeIndex
                    ? "bg-brand-primary/10"
                    : "hover:bg-brand-primary/10",
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-900">
                    {option.name}
                  </span>
                  {hasSecondary && (
                    <span className="block text-xs text-gray-500">
                      {district && (
                        <span className="whitespace-nowrap">{district}</span>
                      )}
                      {district && option.region ? " · " : null}
                      {option.region}
                    </span>
                  )}
                </span>
                {isCurrent && (
                  <>
                    <Check
                      className="mt-0.5 h-4 w-4 shrink-0 text-brand-primary"
                      aria-hidden="true"
                    />
                    <span className="sr-only">
                      {t("orders.locality.picker.current")}
                    </span>
                  </>
                )}
              </li>
            );
          })}
      </ul>

      <p
        id={statusId}
        aria-live="polite"
        className={cn(
          "text-xs",
          statusIsError ? "text-brand-danger" : "text-gray-500",
        )}
      >
        {statusText}
        {statusIsError && (
          <>
            {" "}
            <button
              type="button"
              onClick={() => void search.refetch()}
              className="font-semibold underline hover:no-underline"
            >
              {t("common.retry")}
            </button>
          </>
        )}
      </p>

      {fieldError && (
        <p role="alert" className="text-xs text-brand-danger">
          {fieldError}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
        {status === "manual" && (
          <button
            type="button"
            onClick={resetToAuto}
            aria-disabled={saving || undefined}
            className={cn(TEXT_BUTTON, "text-brand-primary hover:underline")}
          >
            {t("orders.locality.resetToAuto")}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            if (!saving) onClose();
          }}
          aria-disabled={saving || undefined}
          className={cn(TEXT_BUTTON, "text-gray-600 hover:text-gray-900")}
        >
          {t("orders.locality.cancel")}
        </button>
      </div>
    </form>
  );
}
